import { useState } from 'react';
import ImageUpload from './ImageUpload';
import { useAppDispatch } from '@redux/store';
import { uploadFile } from '@services/ApiService';
import { CDocument, IForm } from '@ts/ui-models';
import { TMode } from '@ts/ui-models';
import { useNavigate } from 'react-router';
import { Button, Grid, TextInput, Typography, onChangeObject } from '@ts/storybook';

interface DocumentFormProps extends IForm {}

const DocumentForm = ({ mode, values, requiredFields, onChange }: DocumentFormProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  if (!values) return;
  const [tDocument, setTDocument] = useState<CDocument | null>(new CDocument(values));
  const [state, setState] = useState<{
    showModalPhoto: boolean;
    photoFile: any;
    photoFileURL: any;
  }>({
    showModalPhoto: false,
    photoFile: null,
    photoFileURL: null,
  });
  const { photoFile } = state;

  const isViewMode = mode === 'view';
  const mediaType = photoFile ? photoFile.type : tDocument?.mediaType;

  //#region Handlers
  const onChangeField = field => v => {
    onChangeObject(setTDocument, field, v);

    onChange(field, v);
  };

  const _handleUpdatePhoto = async ({ imageName, purposeId, photoFile }) => {
    const newtDocument = await uploadFile(
      dispatch,
      {
        name: imageName,
        purposeId: purposeId,
        entityTypeId: null,
        entityId: null,
        mediaType: photoFile.type,
        //temporary: true,
        //private: true,
        //scopeCat: 'organization',
        isSystem: false,
      },
      photoFile
    );
    setTDocument(newtDocument);

    setState({ ...state, showModalPhoto: false });
    if (newtDocument) {
      navigate(`/document/${newtDocument.getID()}/edit`, { state: { replace: true } });
    }
  };

  const _handleClearImage = () => {
    onChange('locator', null);
    onChange('imageData', null);
    onChange('mediaType', null);
  };

  //#endregion

  //#region Render
  const renderPhoto = () => {
    const src = tDocument?.locator;

    if (mediaType && !mediaType.includes('image')) {
      return (
        <Grid container size={{ xs: 12 }} justifyContent="center">
          <Typography variant="h6" text={['Document preview not available for type ', mediaType]} />
        </Grid>
      );
    }

    const maxHeight = isViewMode ? 216 : 180;
    if (src) {
      return (
        <Grid container size={{ xs: 12 }} justifyContent="center">
          <img src={src} alt={src} style={{ maxHeight: maxHeight }} />
        </Grid>
      );
    }
  };

  const renderEditPhoto = () => {
    if (isViewMode) return;

    return (
      <Grid size={{ xs: 12 }}>
        <Grid container spacing={2} justifyContent="center">
          <Grid>
            <Button
              label={mediaType ? 'Change' : 'Upload'}
              onClick={() => setState({ ...state, photoFile: null, showModalPhoto: true })}
              disabled={isViewMode}
            />
          </Grid>
          <Grid>
            {!isViewMode && (
              <Button
                variant="outlined"
                label="Clear"
                onClick={() => {
                  if (values.locator) _handleClearImage();
                }}
              />
            )}
          </Grid>
        </Grid>
        <ImageUpload
          imageName={tDocument?.name}
          openModal={state.showModalPhoto}
          onUploadPhoto={_handleUpdatePhoto}
          onClose={() => setState({ ...state, showModalPhoto: false })}
        />
      </Grid>
    );
  };

  //#endregion

  return (
    <Grid container spacing={2} p={4}>
      <Grid size={{ xs: 12 }}>
        <TextInput
          isReadOnly={isViewMode}
          label="Name"
          value={tDocument?.name}
          onChangeValue={onChangeField('name')}
          required={requiredFields?.includes('name')}
          infoText="Blank spaces at the beggining and the end will be trimmed on save"
        />
      </Grid>
      <Grid size={{ xs: 12 }}>{mediaType && <TextInput label="Media type" value={mediaType} isReadOnly={isViewMode} />}</Grid>
      {renderPhoto()}
      {renderEditPhoto()}
    </Grid>
  );
};

export default DocumentForm;
